import type { GameState, Viewport } from "../sim/types";
import { BUFFER_Y, LINE } from "../sim/geometry";
import { FREEZE_MS, INTERMISSION_MS, centreFor } from "../sim/constants";
import { band } from "../sim/bands";
import { sectorNumeral } from "../sim/scene";
import { nearestThreat } from "../sim/sim";
import { C, FONT, setTracking } from "./palette";
import { drawScenery } from "./scenery";
import { drawRock } from "./rocks";
import { drawDebris, drawShip, drawShots } from "./ship";

/**
 * Transicion de ola. La escenografia nueva entra con crossfade mientras el
 * numero de ola aparece entre dos reglas que se abren desde el centro.
 */
function intermission(cx: CanvasRenderingContext2D, s: GameState, vp: Viewport): void {
  const t = 1 - s.inter / INTERMISSION_MS;
  const wipe = Math.min(1, t / 0.35);
  const hold = Math.min(1, Math.max(0, (t - 0.2) / 0.25));
  const out = t > 0.78 ? (t - 0.78) / 0.22 : 0;

  cx.save();
  cx.globalAlpha = 1 - out;

  const sy = vp.h * 0.5;
  const sw = vp.w * wipe;
  cx.strokeStyle = "rgba(95,232,255,0.5)";
  cx.lineWidth = 1;
  cx.beginPath();
  cx.moveTo(vp.w / 2 - sw / 2, sy - 34);
  cx.lineTo(vp.w / 2 + sw / 2, sy - 34);
  cx.stroke();
  cx.beginPath();
  cx.moveTo(vp.w / 2 - sw / 2, sy + 34);
  cx.lineTo(vp.w / 2 + sw / 2, sy + 34);
  cx.stroke();
  cx.fillStyle = "rgba(3,6,10,0.72)";
  cx.fillRect(vp.w / 2 - sw / 2, sy - 34, sw, 68);

  if (hold > 0) {
    cx.globalAlpha = (1 - out) * hold;
    cx.textAlign = "center";
    cx.textBaseline = "middle";

    const scale = 0.9 + 0.1 * hold;
    cx.save();
    cx.translate(vp.w / 2, sy - 8);
    cx.scale(scale, scale);
    cx.font = `700 34px ${FONT}`;
    cx.fillStyle = C.hot;
    setTracking(cx, "0.3em");
    cx.fillText(`OLA ${s.interWave}`, 0, 0);
    cx.restore();

    cx.font = `700 10px ${FONT}`;
    cx.fillStyle = C.cyan;
    setTracking(cx, "0.34em");
    cx.fillText(
      `SECTOR ${sectorNumeral(s.interWave)} \u00B7 ${s.scene.name.toUpperCase()}`,
      vp.w / 2,
      sy + 14,
    );

    cx.font = `10px ${FONT}`;
    cx.fillStyle = C.dim;
    setTracking(cx, "0.18em");
    cx.fillText(
      band(Math.max(1, Math.round(centreFor(s.interWave)))).label.toUpperCase(),
      vp.w / 2,
      sy + 30,
    );
    setTracking(cx, "0px");
  }
  cx.restore();
}

export function draw(
  cx: CanvasRenderingContext2D,
  s: GameState,
  now: number,
  vp: Viewport,
): void {
  cx.fillStyle = C.bg;
  cx.fillRect(0, 0, vp.w, vp.h);

  cx.save();
  if (s.shake > 0.4) {
    cx.translate((Math.random() - 0.5) * s.shake, (Math.random() - 0.5) * s.shake);
  }

  drawScenery(cx, s, now, vp);

  // linea de defensa
  const L = LINE(vp);
  cx.strokeStyle = s.flash > 0.05 ? C.danger : "rgba(30,122,92,0.8)";
  cx.lineWidth = 1.4;
  cx.setLineDash([7, 7]);
  cx.beginPath();
  cx.moveTo(0, L + 0.5);
  cx.lineTo(vp.w, L + 0.5);
  cx.stroke();
  cx.setLineDash([]);

  // las lejanas primero, para que las cercanas las tapen
  const live = s.rocks.filter((r) => r.alive).sort((a, b) => a.y - b.y);
  for (const r of live) drawRock(cx, r, s, now, vp);

  drawShots(cx, s);
  drawDebris(cx, s);
  drawShip(cx, s, now, vp);

  // buffer de entrada
  cx.textAlign = "center";
  cx.textBaseline = "middle";
  cx.font = `700 26px ${FONT}`;
  cx.fillStyle = s.err > 0.05 ? C.danger : s.buffer ? C.hot : "rgba(30,122,92,0.9)";
  setTracking(cx, "0.16em");
  cx.fillText(s.buffer || "\u2013\u2013", vp.w / 2, BUFFER_Y(vp));
  setTracking(cx, "0px");
  cx.restore();

  // congelamiento
  if (s.freeze > 0) {
    cx.fillStyle = "rgba(95,232,255,0.055)";
    cx.fillRect(0, 0, vp.w, vp.h);
    cx.fillStyle = "rgba(95,232,255,0.85)";
    cx.fillRect(0, 0, (s.freeze / FREEZE_MS) * vp.w, 3);
    cx.textAlign = "center";
    cx.font = `700 11px ${FONT}`;
    cx.fillStyle = C.cyan;
    setTracking(cx, "0.3em");
    cx.fillText(`TIEMPO DETENIDO  ${(s.freeze / 1000).toFixed(1)}s`, vp.w / 2, 20);
    setTracking(cx, "0px");
  }

  if (s.inter > 0) intermission(cx, s, vp);

  // aviso de proximidad: gemelo visual del aviso sonoro, para jugar en silencio
  const near = nearestThreat(s, vp);
  if (near > 0.68 && s.freeze <= 0) {
    const k = (near - 0.68) / 0.32;
    const pulse = 0.5 + 0.5 * Math.sin(now * 0.012);
    const g = cx.createRadialGradient(
      vp.w / 2,
      vp.h / 2,
      vp.h * 0.3,
      vp.w / 2,
      vp.h / 2,
      vp.h * 0.82,
    );
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(1, `rgba(255,77,77,${(0.07 + k * 0.28 * pulse).toFixed(3)})`);
    cx.fillStyle = g;
    cx.fillRect(0, 0, vp.w, vp.h);
  }

  if (s.flash > 0.05) {
    cx.fillStyle = `rgba(255,77,77,${(s.flash * 0.22).toFixed(3)})`;
    cx.fillRect(0, 0, vp.w, vp.h);
  }
}
